/*
 * ethers library is used to interact with the smart contract
 * @see https://docs.ethers.io/v5/
 * and the JSON file is used to store the ABI of the smart contract
 * @see https://docs.ethers.io/v5/api/utils/abi/
 */
import { ethers } from "ethers";
import config from "../../../configs";
import trinityManagerArtifact from "../../../configs/abis/trinity-manager.json";
import { request } from "../../../admin/plugins/axios";

const { abi: trinityManagerAbi } = trinityManagerArtifact;
const contractAddress = config.get("smartContract.TRINITY_MANAGER_ADDRESS");

const getContract = (signerOrProvider) => {
  if (!signerOrProvider) {
    throw new Error(
      "A signer or provider is required to interact with TrinityManager"
    );
  }
  return new ethers.Contract(
    contractAddress,
    trinityManagerAbi,
    signerOrProvider
  );
};

export const getMyInfo = async (signerOrProvider) => {
  try {
    const contract = getContract(signerOrProvider);
    const info = await contract.getMyInfo();
    return {
      userAddress: info.userAddress,
      referrer: info.referrer,
      totalShares: Number(info.totalShares || 0),
      directs: info.directs || [],
    };
  } catch (error) {
    console.error("Failed to call getMyInfo", error);
    return {
      userAddress: ethers.constants.AddressZero,
      referrer: ethers.constants.AddressZero,
      totalShares: 0,
      directs: [],
    };
  }
};

/**
 * Get referrer (sponsor) address for any user address
 * @param {ethers.Provider|ethers.Signer} signerOrProvider - Provider or signer
 * @param {string} userAddress - The user address to get referrer for
 * @returns {Promise<string>} The referrer address, or zero address if not found
 */
export const getUserReferrer = async (signerOrProvider, userAddress) => {
  if (!signerOrProvider || !userAddress) {
    console.warn("⚠️ getUserReferrer: Missing signerOrProvider or userAddress");
    return ethers.constants.AddressZero;
  }
  try {
    const contract = getContract(signerOrProvider);
    const userInfo = await contract.users(userAddress);
    if (!userInfo || !userInfo.referrer) {
      return ethers.constants.AddressZero;
    }
    return userInfo.referrer;
  } catch (error) {
    console.error("Failed to get user referrer", error);
    return ethers.constants.AddressZero;
  }
};

export const checkReferrerIsVerified = async (signerOrProvider, refAddress) => {
  if (!refAddress) return false;
  try {
    const contract = getContract(signerOrProvider);
    const result = await contract.checkReferrerIsVerified(refAddress);
    return Boolean(result);
  } catch (error) {
    console.error("Failed to verify referrer", error);
    return false;
  }
};

export const getPriceForShares = async (signerOrProvider, shares) => {
  if (!shares) return ethers.constants.Zero;
  try {
    const contract = getContract(signerOrProvider);
    return await contract.getPriceForShares(
      ethers.BigNumber.from(String(shares))
    );
  } catch (error) {
    console.error("Failed to get price for shares", error);
    return ethers.constants.Zero;
  }
};

export const buyShares = async (signer, shares, referrerAddress) => {
  if (!signer) {
    throw new Error("Signer is required to buy shares");
  }
  const contract = getContract(signer);
  const price = await contract.getPriceForShares(
    ethers.BigNumber.from(String(shares))
  );
  return contract.buyShares(
    ethers.BigNumber.from(String(shares)),
    referrerAddress,
    {
      value: price,
    }
  );
};

export const getPurchaseTransactions = async (
  signerOrProvider,
  userAddress,
  page = 0,
  limit = 10
) => {
  if (!signerOrProvider || !userAddress) {
    return { transactions: [], total: 0 };
  }
  try {
    const contract = getContract(signerOrProvider);
    const offset = Number(page) * Number(limit);
    const [purchases, total] = await contract.getPurchaseTransactions(
      userAddress,
      ethers.BigNumber.from(String(offset)),
      ethers.BigNumber.from(String(limit))
    );
    return {
      transactions: purchases.map((tx) => ({
        id: tx.id ? tx.id.toString() : "0",
        buyer: tx.buyer,
        shares: tx.shares ? tx.shares.toString() : "0",
        amount: tx.amount ? tx.amount.toString() : "0",
        timestamp: tx.timestamp ? Number(tx.timestamp.toString()) : 0,
      })),
      total: Number(total?.toString?.() || 0),
    };
  } catch (error) {
    console.error("Failed to fetch purchase transactions", error);
    return { transactions: [], total: 0 };
  }
};

export const getCommissionTransactions = async (
  signerOrProvider,
  userAddress,
  page = 0,
  limit = 10
) => {
  if (!signerOrProvider || !userAddress) {
    console.warn(
      "⚠️ getCommissionTransactions: Missing signerOrProvider or userAddress"
    );
    return { transactions: [], total: 0 };
  }
  try {
    console.log("📤 getCommissionTransactions - Calling contract:", {
      contractAddress,
      userAddress,
      page,
      limit,
      offset: Number(page) * Number(limit),
    });
    const contract = getContract(signerOrProvider);
    const offset = Number(page) * Number(limit);
    const [commissions, total] = await contract.getCommissionTransactions(
      userAddress,
      ethers.BigNumber.from(String(offset)),
      ethers.BigNumber.from(String(limit))
    );
    console.log("✅ getCommissionTransactions - Contract response:", {
      commissionsCount: commissions?.length || 0,
      total: total?.toString?.() || "0",
      commissions: commissions,
    });
    const result = {
      transactions: commissions.map((tx) => ({
        id: tx.id ? tx.id.toString() : "0",
        from: tx.from,
        to: tx.to,
        relation: tx.relation ? Number(tx.relation.toString()) : 0,
        amount: tx.amount ? tx.amount.toString() : "0",
        timestamp: tx.timestamp ? Number(tx.timestamp.toString()) : 0,
      })),
      total: Number(total?.toString?.() || 0),
    };
    console.log("✅ getCommissionTransactions - Processed result:", result);
    return result;
  } catch (error) {
    console.error("❌ Failed to fetch commission transactions", error);
    console.error("Error details:", {
      message: error?.message,
      reason: error?.reason,
      code: error?.code,
      data: error?.data,
    });
    return { transactions: [], total: 0 };
  }
};

// Legacy fallbacks for components that still expect transaction/commission data.
export const getUserCommissionsList = async () => ({
  transactions: [],
  totalList: 0,
});
export const getUserPurchasedTransactionsList = async () => ({
  transactions: [],
  totalList: 0,
});

/**
 * Get purchase transaction hashes from backend API
 * @param {Array<number>} transactionIds - Array of purchase transaction IDs
 * @returns {Promise<Object>} Object containing transaction hashes
 */
export const getPurchaseHashes = async (transactionIds) => {
  if (
    !transactionIds ||
    !Array.isArray(transactionIds) ||
    transactionIds.length === 0
  ) {
    console.warn("⚠️ getPurchaseHashes: Invalid transaction IDs");
    return { hashes: [] };
  }
  try {
    const apiUrl = config.get("smartContract.API_URL");
    const endpoint = `${apiUrl}/transaction/purchase/get`;
    const payload = {
      transaction_ids: transactionIds,
    };

    console.log("📤 Calling getPurchaseHashes API (POST):", endpoint);
    console.log("📦 Payload:", payload);

    const response = await request("post", endpoint, payload);

    console.log("✅ Purchase hashes response status:", response.status);
    console.log("✅ Purchase hashes response data:", response.data);
    return response.data || { hashes: [] };
  } catch (error) {
    console.error("❌ Failed to get purchase hashes", error);
    console.error("Error details:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      url: error?.config?.url,
    });
    return { hashes: [] };
  }
};

/**
 * Get commission transaction hashes from backend API
 * @param {Array<number>} transactionIds - Array of commission transaction IDs
 * @returns {Promise<Object>} Object containing transaction hashes
 */
export const getCommissionHashes = async (transactionIds) => {
  if (
    !transactionIds ||
    !Array.isArray(transactionIds) ||
    transactionIds.length === 0
  ) {
    console.warn("⚠️ getCommissionHashes: Invalid transaction IDs");
    return { hashes: [] };
  }
  try {
    const apiUrl = config.get("smartContract.API_URL");
    const endpoint = `${apiUrl}/transaction/commission/get`;
    const payload = {
      transaction_ids: transactionIds,
    };

    console.log("📤 Calling getCommissionHashes API (POST):", endpoint);
    console.log("📦 Payload:", payload);

    const response = await request("post", endpoint, payload);

    console.log("✅ Commission hashes response status:", response.status);
    console.log("✅ Commission hashes response data:", response.data);
    return response.data || { hashes: [] };
  } catch (error) {
    console.error("❌ Failed to get commission hashes", error);
    console.error("Error details:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      url: error?.config?.url,
    });
    return { hashes: [] };
  }
};

/**
 * Test function to check if purchase/get and commission/get APIs are working
 * Can be called from browser console: window.testTrinityAPIs()
 * @param {Array<number>} testPurchaseIds - Test purchase transaction IDs (default: [1])
 * @param {Array<number>} testCommissionIds - Test commission transaction IDs (default: [1])
 */
export const testTrinityAPIs = async (
  testPurchaseIds = [1],
  testCommissionIds = [1]
) => {
  console.log("🧪 Testing Trinity APIs...");
  console.log("API URL:", config.get("smartContract.API_URL"));

  const results = {
    purchaseGet: { success: false, error: null, data: null },
    commissionGet: { success: false, error: null, data: null },
  };

  // Test /transaction/purchase/get
  console.log("\n📋 Testing /transaction/purchase/get...");
  try {
    const purchaseResult = await getPurchaseHashes(testPurchaseIds);
    results.purchaseGet = {
      success: true,
      data: purchaseResult,
      error: null,
    };
    console.log("✅ /transaction/purchase/get is working!");
    console.log("Response:", purchaseResult);
  } catch (error) {
    results.purchaseGet = {
      success: false,
      data: null,
      error: {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        url: error?.config?.url,
      },
    };
    console.error("❌ /transaction/purchase/get failed!");
    console.error("Error:", error);
  }

  // Test /transaction/commission/get
  console.log("\n📋 Testing /transaction/commission/get...");
  try {
    const commissionResult = await getCommissionHashes(testCommissionIds);
    results.commissionGet = {
      success: true,
      data: commissionResult,
      error: null,
    };
    console.log("✅ /transaction/commission/get is working!");
    console.log("Response:", commissionResult);
  } catch (error) {
    results.commissionGet = {
      success: false,
      data: null,
      error: {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        url: error?.config?.url,
      },
    };
    console.error("❌ /transaction/commission/get failed!");
    console.error("Error:", error);
  }

  // Summary
  console.log("\n📊 Test Summary:");
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  console.log(
    "Purchase/Get API:",
    results.purchaseGet.success ? "✅ Working" : "❌ Failed"
  );
  if (!results.purchaseGet.success) {
    console.log("  Status:", results.purchaseGet.error?.status);
    console.log("  Message:", results.purchaseGet.error?.message);
  }
  console.log(
    "Commission/Get API:",
    results.commissionGet.success ? "✅ Working" : "❌ Failed"
  );
  if (!results.commissionGet.success) {
    console.log("  Status:", results.commissionGet.error?.status);
    console.log("  Message:", results.commissionGet.error?.message);
  }
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

  return results;
};

// Make it available globally for easy testing from console
if (typeof window !== "undefined") {
  window.testTrinityAPIs = testTrinityAPIs;
  window.getPurchaseHashes = getPurchaseHashes;
  window.getCommissionHashes = getCommissionHashes;
}
